import {
  CATEGORY_HINT,
  CATEGORY_LABEL,
  CATEGORY_STYLE,
  FINDING_CATEGORIES,
  categoryLabel,
  findingsNarrative,
  type FindingCategory,
  type FindingStatus,
  type FindingWithContext,
} from '../../lib/ctoReviews';
import { CtoFindingRow } from './CtoFindingRow';

const COLUMNS: (FindingCategory | null)[] = [...FINDING_CATEGORIES, null];

export function CtoFindingBoard({
  findings,
  onStatus,
}: {
  findings: FindingWithContext[];
  onStatus: (id: string, status: FindingStatus) => void;
}) {
  const narrative = findingsNarrative(findings);

  return (
    <div>
      {narrative ? (
        <p className="text-sm text-gray-700 dark:text-gray-200 mb-4">{narrative}</p>
      ) : null}
      <div className="flex w-full gap-3 items-stretch">
        {COLUMNS.map((category) => {
          const rows = findings.filter((f) =>
            category === null ? !f.category : f.category === category,
          );
          if (category === null && rows.length === 0) return null;
          const empty = rows.length === 0;
          const open = rows.filter((f) => f.status === 'open').length;
          const hint = category ? CATEGORY_HINT[category] : undefined;
          return (
            <section
              key={category ?? 'uncategorized'}
              title={empty ? `${categoryLabel(category)} — nothing to see here` : undefined}
              className={`flex flex-col rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 min-h-[16rem] ${
                empty ? 'w-11 shrink-0' : 'min-w-0 flex-1'
              }`}
            >
              {empty ? (
                <div className="flex-1 flex flex-col items-center py-2.5 px-1 gap-3">
                  {category ? (
                    <span
                      className={`text-[10px] leading-none px-1 py-1 rounded ${CATEGORY_STYLE[category]}`}
                      title={hint}
                    >
                      {CATEGORY_LABEL[category].slice(0, 1)}
                    </span>
                  ) : null}
                  <p
                    className="flex-1 text-[11px] text-gray-400 tracking-wide whitespace-nowrap"
                    style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                  >
                    Nothing to see here
                  </p>
                </div>
              ) : (
                <>
                  <header className="px-3 py-2.5 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between gap-2">
                      <h3
                        className="text-sm font-semibold text-gray-900 dark:text-white truncate"
                        title={hint}
                      >
                        {categoryLabel(category)}
                      </h3>
                      <span className="text-[11px] text-gray-400 shrink-0">
                        {open} open · {rows.length}
                      </span>
                    </div>
                    {category ? (
                      <p
                        className={`mt-1 text-[11px] px-1.5 py-0.5 rounded inline-block ${CATEGORY_STYLE[category]}`}
                        title={hint}
                      >
                        {CATEGORY_LABEL[category]}
                      </p>
                    ) : null}
                  </header>
                  <ul className="divide-y divide-gray-100 dark:divide-gray-700 overflow-y-auto max-h-[36rem] bg-white dark:bg-gray-800 rounded-b-xl">
                    {rows.map((finding) => (
                      <CtoFindingRow
                        key={finding.id}
                        finding={finding}
                        onStatus={(status) => onStatus(finding.id, status)}
                      />
                    ))}
                  </ul>
                </>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function SegmentedToggle<T extends string>({
  value,
  onChange,
  items,
  ariaLabel,
}: {
  value: T | 'all';
  onChange: (value: T) => void;
  items: { id: T; label: string; hint?: string }[];
  ariaLabel: string;
}) {
  return (
    <div className="inline-flex rounded-xl shadow-sm" role="group" aria-label={ariaLabel}>
      {items.map((item, index) => (
        <span key={item.id} className="relative inline-flex group">
          <button
            type="button"
            aria-pressed={value === item.id}
            onClick={() => onChange(item.id)}
            className={`px-3.5 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 ${
              index === 0 ? 'rounded-l-xl' : '-ml-px'
            } ${index === items.length - 1 ? 'rounded-r-xl' : ''} ${
              value === item.id
                ? 'relative z-10 bg-fuchsia-600 border-fuchsia-600 text-white'
                : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            {item.label}
          </button>
          {item.hint ? (
            <span
              role="tooltip"
              className="pointer-events-none absolute left-1/2 top-full z-40 mt-2 w-56 -translate-x-1/2 rounded-xl border border-gray-700 bg-gray-900 px-3.5 py-2.5 text-left text-sm text-white opacity-0 shadow-lg invisible group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
            >
              {item.hint}
            </span>
          ) : null}
        </span>
      ))}
    </div>
  );
}

export type FindingView = 'table' | 'narrative' | 'graph';

const VIEW_HINTS: Record<FindingView, string> = {
  table: 'The working list. Scan, act, and close findings.',
  narrative: 'How ideas sit next to each other.',
  graph: 'Effort against impact — high to low — so you can see the spread.',
};

export function CtoFindingViewToggle({
  view,
  onChange,
}: {
  view: FindingView;
  onChange: (view: FindingView) => void;
}) {
  return (
    <SegmentedToggle
      ariaLabel="Finding view"
      value={view}
      onChange={onChange}
      items={[
        { id: 'table', label: 'Table', hint: VIEW_HINTS.table },
        { id: 'narrative', label: 'Narrative', hint: VIEW_HINTS.narrative },
        { id: 'graph', label: 'Graph', hint: VIEW_HINTS.graph },
      ]}
    />
  );
}

export function CtoImpactToggle({
  value,
  onChange,
}: {
  value: 'all' | 'high' | 'low';
  onChange: (value: 'all' | 'high' | 'low') => void;
}) {
  return (
    <SegmentedToggle
      ariaLabel="Filter by impact"
      value={value}
      onChange={(next) => onChange(value === next ? 'all' : next)}
      items={[
        { id: 'high', label: 'High impact', hint: 'Impact 4 or 5 — worth rearranging a week for.' },
        { id: 'low', label: 'Low impact', hint: 'Impact 1 to 3. Still stored; not the pile to start with.' },
      ]}
    />
  );
}

export function CtoEffortToggle({
  value,
  onChange,
}: {
  value: 'all' | 'low' | 'high';
  onChange: (value: 'all' | 'low' | 'high') => void;
}) {
  return (
    <SegmentedToggle
      ariaLabel="Filter by effort"
      value={value}
      onChange={(next) => onChange(value === next ? 'all' : next)}
      items={[
        { id: 'low', label: 'Low effort', hint: 'Trivial or hours — an afternoon.' },
        { id: 'high', label: 'High effort', hint: 'Days or weeks. A project, not a pass.' },
      ]}
    />
  );
}
